/**
 * Mega Menu Component
 *
 * Full-width mega menu with teleportation, backdrop, and dynamic alignment.
 * Automatically closes other menus when one opens via a custom event bus.
 *
 * Usage in Liquid:
 * <div x-data="megaMenu" class="relative">
 *   <a x-ref="btn" @click.stop="toggle">Menu</a>
 *   <div x-show="open" @click="close"></div>
 *   <template x-teleport="body">
 *     <div x-show="open" :style="style">Content</div>
 *   </template>
 * </div>
 */

import type { Magics } from 'alpinejs';

interface MegaMenuData {
    uid: string;
    open: boolean;
    style: string;
    gap: number;
    init(this: MegaMenuContext): void;
    align(this: MegaMenuContext): void;
    toggle(this: MegaMenuContext): void;
    close(this: MegaMenuContext): void;
}

type MegaMenuContext = MegaMenuData & Magics<MegaMenuData>;

export default function megaMenu() {
    return {
        uid: Math.random().toString(36).slice(2),
        open: false,
        style: '',
        gap: 8,

        init() {
            const uid = this.uid;
            const align = this.align.bind(this);
            const open = () => this.open;

            // Listen for custom 'close-all-flyouts' event to close other menus
            window.addEventListener('close-all-flyouts', (e) => {
                if (e.detail !== uid) {
                    this.open = false;
                }
            });

            // Re-align on resize and scroll when menu is open
            const realign = () => {
                if (this.open) {
                    align();
                }
            };

            window.addEventListener('resize', realign);
            window.addEventListener('scroll', realign, { passive: true });
        },

        align() {
            const r = this.$refs.btn.getBoundingClientRect();
            const top = r.bottom + this.gap;
            this.style = 'position:fixed; left:0; right:0; top:' + top + 'px;';
        },

        toggle() {
            if (this.open) {
                this.open = false;
                return;
            }
            window.dispatchEvent(new CustomEvent('close-all-flyouts', { detail: this.uid }));
            this.align();
            requestAnimationFrame(() => {
                this.open = true;
            });
        },

        close() {
            this.open = false;
        },
    } as MegaMenuData;
}
